import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FlatList,
  Platform,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import { Modal } from '@/components/ui/app-modal';
import Ionicons from '@expo/vector-icons/Ionicons';
import {
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@/components/ui/avatar';
import { CompactSwitch } from '@/components/ui/compact-switch';
import { Loading } from '@/components/ui/loading';
import { Logo } from '@/components/brand/logo';
import { isCompactWidth } from '@/constants/layout';
import { formatCompactDuration } from '@/lib/duration';
import { systemMessageText } from '@/lib/system-message';
import { useOpenClock } from '@/hooks/use-open-clock';
import { captureException } from '@/lib/telemetry';
import { rememberGroupHeader } from '@/lib/group-cache';
import {
  isOpenNow,
  openMinutesLeft,
  OPEN_TO_CHAT_MINUTES,
} from '@/lib/open-to-chat';
import { socialStrings as strings } from '@/i18n/features/social.i18n';
import { useAuthStore } from '@/state/auth-store';
import { inboxOrder, nextInactiveLoad } from '@/state/inbox-sync';
import { useSessionStore } from '@/state/session-store';
import { useAppTheme } from '@/theme/app-theme';
import type { TranslationKey } from '@/i18n';
import type { ChatSession, SmallGroupInbox } from '@/types';

type InboxItem =
  | { kind: 'group'; id: string; updatedAt: string; group: SmallGroupInbox }
  | { kind: 'chat'; id: string; updatedAt: string; chat: ChatSession };

// Open sits FIRST because it is the tab with a reason to be checked now: its
// contents are leases measured in hours, while Active and Inactive are stable
// for days. It is a strict subset of Active, never a separate audience.
type InboxTab = 'open' | 'active' | 'inactive';

const FONT_FAMILY = Platform.select({
  ios: 'Avenir Next',
  android: 'sans-serif',
  default: undefined,
});

const BRAND_BLUE = '#2468D8';
const BRAND_BLUE_SHADOW = '#205BBC';

function getCuesColors(isDark: boolean) {
  return {
    screen: isDark ? '#0D1724' : '#FCFDFF',
    topBar: isDark ? '#101C2C' : '#FCFDFF',

    surface: isDark ? '#152235' : '#FFFFFF',
    surfaceMuted: isDark
      ? '#19283B'
      : '#FAFBFD',
    surfaceSoft: isDark
      ? '#1B2C43'
      : '#F4F8FF',
    surfacePressed: isDark
      ? '#213650'
      : '#F4F8FF',

    textPrimary: isDark
      ? '#EFF5FF'
      : '#102D51',
    textStrong: isDark
      ? '#DFEAFA'
      : '#153252',
    textBody: isDark
      ? '#C0CDDD'
      : '#425873',
    textSecondary: isDark
      ? '#A8B6C9'
      : '#7B879A',
    textMuted: isDark
      ? '#8999AD'
      : '#929DAC',

    border: isDark
      ? '#2C3C51'
      : '#E1E8F1',
    borderSoft: isDark
      ? '#29394D'
      : '#E4EAF2',
    divider: isDark
      ? '#2A3A4E'
      : '#E3E9F1',

    primary: isDark
      ? '#6EA8FF'
      : '#2468D8',
    primaryStrong: isDark
      ? '#91BCFF'
      : '#2463CB',
    primaryText: isDark
      ? '#B9D3FF'
      : '#3568B7',
    primarySoft: isDark
      ? '#203754'
      : '#F0F5FD',
    primarySurface: isDark
      ? '#1D314B'
      : '#F3F7FF',
    primaryBorder: isDark
      ? '#34577F'
      : '#DDE7F7',

    iconNeutral: isDark
      ? '#A9B9CD'
      : '#526C8D',

    green: isDark
      ? '#57C98D'
      : '#35A86C',
    // The same pair `home.tsx` gives its visibility toggle, because it is now
    // the same control in two places.
    greenSoft: isDark
      ? '#19392E'
      : '#F1FAF5',
    greenBorder: isDark
      ? '#2F634C'
      : '#D7EADF',

    shadow: isDark
      ? '#000000'
      : '#7187A6',

    white: '#FFFFFF',
  };
}

type CuesColors =
  ReturnType<typeof getCuesColors>;

type CuesStyles =
  ReturnType<typeof createStyles>;

/**
 * Fire off a page append and report it if it fails.
 *
 * `onEndReached` cannot await anything, so every page append here is
 * fire-and-forget. What it must not be is fire-and-forget-QUIETLY: all four of
 * these store actions rethrow, and a bare `void` turns a failed append into an
 * unhandled rejection.
 *
 * That is not invisible — checked, rather than assumed: on Hermes the Sentry
 * SDK enables `enablePromiseRejectionTracker` by default and reports these,
 * deliberately marked `handled: true` so they do not distort the crash-free
 * rate. So the gap is attribution, not silence. What arrives is a synthetic
 * stack with no scope, indistinguishable from any other rejection in the app,
 * next to a `Possible Unhandled Promise Rejection` warning in development.
 * With a scope it is one named thing: which tab, which direction, how often.
 *
 * Nothing is shown to the user and nothing is retried, and both are deliberate.
 * The list on screen is intact — only the NEXT page is missing — so an error
 * state would replace something true with an apology. The cursor is left
 * untouched by the failure, so the next `onEndReached` simply asks again; a
 * retry loop here would hammer a failing endpoint for a screen the user can fix
 * by scrolling.
 */
function appendPage(scope: string, load: Promise<void>): void {
  void load.catch((error) => captureException(error, { scope }));
}

function formatInboxDate(
  value: string | null | undefined,
  locale: string,
): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

export default function Inbox() {
  const router = useRouter();
  const params = useLocalSearchParams<{ tab?: string }>();
  const { t } = useTranslation();
  const { isDark } = useAppTheme();

  // `useWindowDimensions` rather than a one-time `Dimensions.get`, because on
  // web the window is resized while the screen is mounted.
  const { width } = useWindowDimensions();
  const compact = isCompactWidth(width);

  // The wordmark is absolutely positioned, so it does not compete for space
  // with the centred filter chips — it sits UNDER them. Once the viewport is
  // phone-sized the chips reach back far enough to overlap it and the header
  // reads as broken text.
  const showLogo = Platform.OS === 'web' && !compact;

  const colors = useMemo(
    () => getCuesColors(isDark),
    [isDark],
  );

  const styles = useMemo(
    () => createStyles(colors, isDark, compact),
    [colors, isDark, compact],
  );

  const chats = useSessionStore(
    (state) => state.chats,
  );

  const chatSectionTotals = useSessionStore(
    (state) => state.chatSectionTotals,
  );

  const loadMoreChats = useSessionStore(
    (state) => state.loadMoreChats,
  );
  const loadMoreInactiveGroups = useSessionStore(
    (state) => state.loadMoreInactiveGroups,
  );
  const chatInactiveCursor = useSessionStore(
    (state) => state.chatCursors.inactive,
  );
  const groupInactiveCursor = useSessionStore(
    (state) => state.groupInactiveCursor,
  );
  const groupInactiveTotal = useSessionStore(
    (state) => state.groupInactiveTotal,
  );

  const groupInbox = useSessionStore(
    (state) => state.groupInbox,
  );

  const unreadByChat = useSessionStore(
    (state) => state.unreadByChat,
  );

  const refreshChats = useSessionStore(
    (state) => state.refreshChats,
  );

  const refreshGroupInbox = useSessionStore(
    (state) => state.refreshGroupInbox,
  );

  const openChats = useSessionStore((state) => state.openChats);
  const openTotal = useSessionStore((state) => state.openTotal);
  const openSettled = useSessionStore((state) => state.openSettled);
  // Load-bearing again: it is what separates "this section is empty" from
  // "this device has not seen all of it yet". See `ListEmptyComponent`.
  const openCursor = useSessionStore((state) => state.openCursor);
  // And this separates both of those from "we could not ask".
  const openFailed = useSessionStore((state) => state.openFailed);
  const refreshOpenChats = useSessionStore((state) => state.refreshOpenChats);
  const loadMoreOpenChats = useSessionStore((state) => state.loadMoreOpenChats);

  // MY own signal, read from the auth user rather than kept here: it arrives
  // with the session bootstrap, so the toggle is right on first paint instead
  // of after a request. See `setOpenToChat` in the auth store.
  const myOpenUntil = useAuthStore((state) => state.user?.openToChatUntil ?? null);
  const setOpenToChat = useAuthStore((state) => state.setOpenToChat);
  const [durationOpen, setDurationOpen] = useState(false);
  const [togglePending, setTogglePending] = useState(false);

  const [refreshing, setRefreshing] =
    useState(false);

  const [tab, setTab] =
    useState<InboxTab>(
      params.tab === 'inactive' || params.tab === 'open' ? params.tab : 'active',
    );

  /**
   * The clock every Open comparison on this screen is made against.
   *
   * It began as plain state sampled only when data landed, and the comment
   * defending that called an arc outliving its lease "bounded by a focus or a
   * pull-to-refresh". On a tab somebody is watching, neither happens: it was
   * not a bound. Two separate things went wrong and they needed two separate
   * answers, which is why `useOpenClock` takes two arguments.
   *
   *   * TRUTH. A lapsed contact stayed in a list titled "open to chat", and an
   *     arc stayed on a peer who had gone. Answered by the leases below —
   *     everything this screen can currently show as open, so the clock moves
   *     on the boundary and the row falls out.
   *   * THE LABEL. "I'm Open · for 30m" is derived from the same clock, so it
   *     did not expire late, it STOPPED. Scheduling to expiries alone left it
   *     frozen for the whole lease and then blank. Answered by `countdownFor`,
   *     which earns a wakeup per displayed minute — and only while the label
   *     is on screen, because peers' arcs have no text to step.
   *
   * Neither costs a request. The lease is a timestamp already in hand and
   * already known to the server; every tick here is arithmetic against it.
   */
  const openLeases = useMemo(
    () => [myOpenUntil, ...openChats.map((c) => c.peerOpenUntil), ...chats.map((c) => c.peerOpenUntil)],
    [myOpenUntil, openChats, chats],
  );
  const { nowMs, resample } = useOpenClock(
    openLeases,
    // The countdown is drawn by `OpenToggle`, which only exists in the Open
    // tab's header. Off that tab there is no label to step, so no per-minute
    // wakeup is earned.
    tab === 'open' ? myOpenUntil : null,
  );


  // A navigation that names a tab switches to it — as a render-time
  // adjustment, not an effect. The effect this replaces set state
  // synchronously after the commit, which is a second render for every
  // param change; adjusting during render re-renders once, and only when
  // the param actually moved.
  const [seenParamTab, setSeenParamTab] = useState(params.tab);
  if (params.tab !== seenParamTab) {
    setSeenParamTab(params.tab);
    if (
      params.tab === 'active' ||
      params.tab === 'inactive' ||
      params.tab === 'open'
    ) {
      setTab(params.tab);
    }
  }

  // An empty inbox and an inbox not yet fetched render identically, so the
  // empty state is gated on the fetches having SETTLED — from the store, not a
  // local flag: `refreshAll` runs these same fetches at session load, and a
  // local flag that cannot see them held a first visit on a full-screen
  // spinner re-asking a question the store had already answered (measured at
  // ~1s on device). The focus fetch below still runs; it refreshes in place
  // behind whatever the store can already paint.
  const loaded = useSessionStore(
    (state) => state.chatsSettled && state.groupInboxSettled,
  );

  useFocusEffect(
    useCallback(() => {
      void (async () => {
        // allSettled: one failing call must not hold the screen on a spinner.
        await Promise.allSettled([refreshChats(), refreshGroupInbox()]);
        resample();
      })();
    }, [refreshChats, refreshGroupInbox, resample]),
  );

  // Open is fetched only while its own tab is showing, and again each time it
  // is opened. Two reasons, and they point the same way: it is not on the path
  // to first paint, so loading it at launch would add a sixth request to an
  // arrival that was deliberately tuned down to five; and its rows are leases
  // measured in hours, so a copy loaded when the app started is the one thing
  // this tab must not show.
  useFocusEffect(
    useCallback(() => {
      if (tab !== 'open') return;
      // Caught for the same reason as the expiry re-read below: an async IIFE
      // behind a bare `void` makes a failed read an unhandled rejection. The
      // sibling focus effect above avoids that with `allSettled`; this one has
      // a single call, so a `catch` is the same guarantee.
      refreshOpenChats()
        .then(resample)
        .catch((error) =>
          captureException(error, { scope: 'inbox.openFocusRead' }),
        );
    }, [tab, refreshOpenChats, resample]),
  );

  async function onRefresh() {
    setRefreshing(true);

    try {
      await Promise.all([
        refreshChats(),
        refreshGroupInbox(),
        // Pulled down on the Open tab, the section the user is looking at has
        // to be one of the things that refreshes.
        ...(tab === 'open' ? [refreshOpenChats()] : []),
      ]);
      resample();
    } finally {
      setRefreshing(false);
    }
  }

  // A conversation that ENDED is not Inactive. It stays on Active for its short
  // read-only window and then disappears, because there is nothing to come back
  // to — whereas the Inactive tab is explicitly the place things are kept.
  const inactiveChats = useMemo(
    () => chats.filter((chat) => chat.state === 'inactive'),
    [chats],
  );

  const activeChats = useMemo(
    () => chats.filter((chat) => chat.state !== 'inactive'),
    [chats],
  );

  // The Inactive tab's group half, split once: the merged timeline and the
  // paging rule both read it, and the paging rule needs the OLDEST loaded row
  // of each source, not of the mixture.
  const inactiveGroupRows = useMemo(
    () => groupInbox.filter((group) => (group.state ?? 'active') === 'inactive'),
    [groupInbox],
  );

  // One timeline per tab, ordered by latest visible activity — a group and a
  // one-to-one chat compete for position on the same key, so the row with the
  // newest message is the first row, whichever kind it is. This replaced
  // concatenating groups above chats ("time-limited, so they outrank"): under
  // that layout the unread thing a reader was hunting could sit below every
  // group on screen. The rule and its tie-breaks are `inboxOrder`; both DTOs
  // ship the same `updatedAt` semantics for it. Groups still split by `state`
  // into the tab that matches; an absent `state` (server built before the
  // lifecycle) reads as active, which only ever errs toward the old behaviour.

  const items: InboxItem[] = useMemo(() => {
    // Open is one-to-one only. A Small Group has no "I'm free now" signal —
    // there is no single person to be free — so mixing group rows in would
    // answer a question the tab did not ask.
    if (tab === 'open') {
      // Filtered against the clock, not rendered as fetched. `useOpenClock`
      // already re-judges the list on the boundary, so in practice this drops
      // a row the instant its lease passes — but it is also the backstop for
      // the case a timer cannot cover: the app was suspended, or the page was
      // fetched a moment before an expiry. A list titled "open to chat" must
      // never hold somebody who is not.
      return openChats
        .filter((chat) => isOpenNow(chat.peerOpenUntil, nowMs))
        .map(
          (chat): InboxItem => ({
            kind: 'chat',
            id: chat.id,
            updatedAt: chat.updatedAt,
            chat,
          }),
        );
    }
    const wanted = tab === 'inactive' ? 'inactive' : 'active';
    const groupRows = groupInbox
      .filter((group) => (group.state ?? 'active') === wanted)
      .map(
        (group): InboxItem => ({
          kind: 'group',
          id: group.id,
          updatedAt: group.updatedAt,
          group,
        }),
      );
    const chatRows = (tab === 'inactive' ? inactiveChats : activeChats).map(
      (chat): InboxItem => ({
        kind: 'chat',
        id: chat.id,
        updatedAt: chat.updatedAt,
        chat,
      }),
    );
    return [...groupRows, ...chatRows].sort(inboxOrder);
  }, [tab, groupInbox, activeChats, inactiveChats, openChats, nowMs]);

  // From the server, not from the loaded arrays. Each array is one page now,
  // so a `length` would mean "how many have downloaded" — wrong on the badge,
  // and wrong in the a11y label below, which announces it as a total. BOTH
  // sources: the tab renders dormant conversations and ended groups on one
  // timeline, and a count covering only one of them would be a number that is
  // simply false. Active deliberately has no user-facing count.
  const inactiveCount = chatSectionTotals.inactive + groupInactiveTotal;

  // The server's section total, minus the loaded rows whose leases have since
  // passed. Announcing the raw total would promise a screen reader more open
  // contacts than the list can show — the same defect as a badge over an empty
  // tab, heard instead of seen.
  //
  // Stated precisely, because it is not exact and should not be claimed to be:
  // only LOADED rows can be judged, so a contact on page two whose lease ran
  // out is still counted until the next read. What closes that is the effect
  // below — any local expiry triggers a re-read, which returns a fresh total —
  // so the overstatement is bounded by the gap between an expiry and the
  // request it triggers, not by how long the screen has been open. It can never
  // overstate what is on screen, which is the number a reader is being told
  // about.
  const openLapsed = openChats.filter(
    (chat) => !isOpenNow(chat.peerOpenUntil, nowMs),
  ).length;
  const openCount = Math.max(0, openTotal - openLapsed);

  /**
   * Re-read Open when time has eaten into the loaded page.
   *
   * Filtering lapsed rows out at render fixed one bug and opened another, of a
   * class this codebase has fought before: a FILTERED PAGE LEAVES A HOLE. The
   * list only grows through `onEndReached`, so if the rows on screen lapse
   * while somebody watches, nothing scrolls, nothing fetches, and the tab shows
   * "Nobody is open right now" — while `openCursor` still points at contacts
   * who are. The emptier it gets, the less able it is to refill itself.
   *
   * So the client stops guessing and asks the server, which applies the
   * predicate in SQL and answers with a correct first page and a true total.
   *
   * This is NOT a countdown doing network work. It is keyed on the identity of
   * the lapsed set, so it fires once per expiry boundary — not once per tick,
   * and never while nothing has expired. After the re-read those rows are gone,
   * the key becomes empty, and it settles. Bounded by how many contacts were
   * open at once, spread over the hours their leases run.
   */
  const lapsedKey = useMemo(
    () =>
      openChats
        .filter((chat) => !isOpenNow(chat.peerOpenUntil, nowMs))
        .map((chat) => chat.id)
        .sort()
        .join(','),
    [openChats, nowMs],
  );

  useEffect(() => {
    // Only while the tab is showing: refreshing a list nobody is looking at
    // spends a request to fix a screen that is about to re-fetch on focus.
    if (tab !== 'open' || lapsedKey === '') return;
    // `.catch`, not a bare `void`. `refreshOpenChats` rethrows, and a `.then`
    // with nothing after it turns a failed read into an unhandled rejection —
    // invisible in production and noisy in development, which is the worst
    // pair. Reported rather than swallowed: this is the store's own idiom
    // (`refreshAll` passes `captureException` for exactly this).
    //
    // Failing here no longer strands the tab. It used to: this effect is keyed
    // on `lapsedKey`, which a failed read leaves unchanged, so nothing retried
    // — and the empty state was already reachable. That is fixed at the
    // rendering end instead of with a retry loop, by refusing to claim the
    // section is empty while the cursor says otherwise. See `ListEmptyComponent`.
    refreshOpenChats()
      .then(resample)
      .catch((error) =>
        captureException(error, { scope: 'inbox.openExpiryReread' }),
      );
  }, [tab, lapsedKey, refreshOpenChats, resample]);

  return (
    <SafeAreaView
      style={styles.safe}
      edges={['top']}>
      <StatusBar
        style={isDark ? 'light' : 'dark'}
      />

      <View style={styles.topBar}>
        {showLogo ? (
          <View style={styles.topBarLogo}>
            <Logo size={24} />
          </View>
        ) : null}

        <View
          style={styles.tabs}
          accessibilityRole="tablist">
          <InboxTabButton
            label={t(strings.tabOpen)}
            // Counted in the a11y label only, like Inactive: the visible chip
            // stays a word so the three labels keep equal widths.
            a11yLabel={t(strings.tabOpenA11y, { total: openCount })}
            selected={tab === 'open'}
            onPress={() => setTab('open')}
            colors={colors}
            styles={styles}
          />

          <InboxTabButton
            label={t(strings.tabActive)}
            a11yLabel={t(strings.tabActiveA11y)}
            selected={tab === 'active'}
            onPress={() => setTab('active')}
            colors={colors}
            styles={styles}
          />

          <InboxTabButton
            label={t(strings.tabInactive)}
            // Still counted here, and only here: see the note on the component.
            a11yLabel={t(strings.tabInactiveA11y, { total: inactiveCount })}
            selected={tab === 'inactive'}
            onPress={() => setTab('inactive')}
            colors={colors}
            styles={styles}
          />
        </View>
      </View>

      <FlatList
        data={items}
        keyExtractor={(item) => `${item.kind}-${item.id}`}
        showsVerticalScrollIndicator={false}
        // The tab decides which section to extend; they page independently, so
        // scrolling Inactive must never pull Active's next page.
        onEndReached={() => {
          if (tab === 'open') {
            appendPage('inbox.openLoadMore', loadMoreOpenChats());
            return;
          }
          if (tab === 'active') {
            // Active groups always arrive whole; only chats page here.
            appendPage('inbox.activeLoadMore', loadMoreChats('live'));
            return;
          }
          // One merged timeline, two independently-paged sources. The rule —
          // advance the source whose loaded frontier is newer, because ITS
          // unloaded rows may still belong above the other's loaded ones — is
          // `nextInactiveLoad`, shared and tested in inbox-sync.ts.
          const oldest = (rows: { updatedAt: string }[]) =>
            rows.length === 0
              ? null
              : rows.reduce((a, b) =>
                  Date.parse(a.updatedAt) <= Date.parse(b.updatedAt) ? a : b,
                ).updatedAt;
          const advance = nextInactiveLoad(
            { frontier: oldest(inactiveChats), more: chatInactiveCursor !== null },
            {
              frontier: oldest(inactiveGroupRows),
              more: groupInactiveCursor !== null,
            },
          );
          if (advance === 'chat')
            appendPage('inbox.inactiveChatLoadMore', loadMoreChats('inactive'));
          else if (advance === 'group')
            appendPage('inbox.inactiveGroupLoadMore', loadMoreInactiveGroups());
        }}
        onEndReachedThreshold={0.5}
        contentContainerStyle={[
          styles.list,
          items.length === 0 &&
            styles.emptyList,
        ]}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
            // The page's own background, not `surface`: Android shows the
            // spinner's plate from the first few px of drag, BEFORE the arc
            // has any sweep — a surface-coloured plate reads as an empty dark
            // circle sliding over the list. Painted in `background` the plate
            // is invisible against the page, so what the user sees appear is
            // the arc itself, already drawing. (A progressViewOffset would
            // make it worse: the plate translates from `offset - diameter`,
            // so pushing it down pops it in fully visible even earlier.)
            progressBackgroundColor={
              colors.screen
            }
          />
        }
        ListHeaderComponent={
          tab === 'open' ? (
            // Rendered whether or not the list has rows. The CTA is the point
            // of this tab for somebody with nobody open — putting it inside
            // the non-empty branch would hide the one action available exactly
            // when it is the only thing to do.
            <View style={styles.openHeader}>
              <Text style={styles.openHeading}>{t(strings.openHeading)}</Text>
              <OpenToggle
                openUntil={myOpenUntil}
                nowMs={nowMs}
                pending={togglePending}
                onTurnOn={() => setDurationOpen(true)}
                onTurnOff={async () => {
                  setTogglePending(true);
                  try {
                    await setOpenToChat(null);
                  } finally {
                    setTogglePending(false);
                  }
                }}
                colors={colors}
                styles={styles}
              />
            </View>
          ) : tab === 'active' && items.length > 0 ? (
            <View style={styles.headerPracticeSlot}>
              <PracticeInboxCard
                colors={colors}
                styles={styles}
                compact
                onPress={() => router.push('/practice')}
              />
            </View>
          ) : null
        }
        renderItem={({ item }) =>
          item.kind === 'group' ? (
            <GroupRow
              group={item.group}
              onPress={() => {
                // The row already holds everything the group screen's header
                // needs, so hand it over rather than making it refetch and
                // flash a placeholder in the meantime.
                rememberGroupHeader(item.group.id, item.group);
                router.push({
                  pathname: '/group/[id]',
                  params: { id: item.group.id, fromTab: tab },
                });
              }}
              colors={colors}
              styles={styles}
            />
          ) : (
            <ChatRow
              chat={item.chat}
              unreadCount={unreadByChat[item.chat.id] ?? 0}
              nowMs={nowMs}
              onPress={() =>
                router.push(`/chat/${item.chat.id}`)
              }
              colors={colors}
              styles={styles}
            />
          )
        }
        ListEmptyComponent={
          // Open has its OWN settled flag. Sharing `loaded` would show this
          // tab's empty state as soon as the inbox had answered, which is a
          // different question — and would claim "nobody is open" before
          // anything had asked.
          tab === 'open' ? (
            // Three states, not two, because "Nobody is open right now" is a
            // CLAIM about other people and only one of these justifies it.
            //
            // The previous version gated on `openSettled` plus a null cursor
            // and called that "fully loaded". It is not: `openSettled` is set
            // in a `finally`, so it is true after a read that THREW, and the
            // cursor is still at its initial null because nothing ever came
            // back. A first load that failed therefore announced that nobody
            // was open, having never successfully read anything.
            //
            //   * nothing has finished yet        -> Loading
            //   * the last read failed            -> say so, offer a retry
            //   * more sits behind the cursor     -> Loading; the re-read that
            //     local expiry triggered is fetching it, and claiming empty
            //     would hide exactly those rows
            //   * otherwise                       -> genuinely empty
            !openSettled ? (
              <Loading color={colors.primary} />
            ) : openFailed ? (
              <OpenLoadFailed
                onRetry={() =>
                  refreshOpenChats()
                    .then(resample)
                    .catch((error) =>
                      captureException(error, { scope: 'inbox.openRetry' }),
                    )
                }
                colors={colors}
                styles={styles}
              />
            ) : openCursor !== null ? (
              <Loading color={colors.primary} />
            ) : (
              <EmptyOpen styles={styles} />
            )
          ) : !loaded ? (
            <Loading color={colors.primary} />
          ) : tab === 'inactive' ? (
            <EmptyInactive styles={styles} />
          ) : (
            <EmptyCues
              colors={colors}
              styles={styles}
              onPractice={() => router.push('/practice')}
            />
          )
        }
      />

      <OpenDurationSheet
        visible={durationOpen}
        onDismiss={() => setDurationOpen(false)}
        onChoose={async (minutes) => {
          setDurationOpen(false);
          setTogglePending(true);
          try {
            await setOpenToChat(minutes);
            // Re-read the section straight away: turning myself on does not
            // change who else is open, but it is the moment somebody is most
            // likely to be looking at this list, and the leases it holds may
            // have lapsed since it loaded.
            await refreshOpenChats();
          } finally {
            setTogglePending(false);
          }
        }}
        colors={colors}
        styles={styles}
      />
    </SafeAreaView>
  );
}

/**
 * Conversation Practice entry point, rendered as the first inbox conversation
 * row — same shape/size as a real chat row (avatar, name, preview), with a
 * subtle dark-blue tint so it stands out slightly as a pinned, private thread.
 */
function PracticeInboxCard({
  colors,
  styles,
  onPress,
  compact = false,
}: {
  colors: CuesColors;
  styles: CuesStyles;
  onPress: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  // On top of real conversations the practice entry is just the first row, so it
  // borrows the standard chat-row layout (avatar + info column + chevron) with no
  // special tint or width. The expanded card below is reserved for the empty state.
  if (compact) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(strings.openPracticeA11y)}
        onPress={onPress}
        style={({ hovered, pressed }) => [
          styles.row,
          Platform.OS === 'web' && hovered && styles.rowHovered,
          pressed && styles.rowPressed,
        ]}
      >
        <View style={styles.avatarWrap}>
          <View
            style={[
              practiceCardStyles.avatarCompact,
              { backgroundColor: colors.primary },
            ]}
          >
            <Ionicons name="chatbubbles-outline" size={26} color={colors.white} />
          </View>
        </View>

        <View style={styles.info}>
          <View style={styles.nameRow}>
            <Text numberOfLines={1} style={styles.name}>
              {t(strings.practiceTitle)}
            </Text>
          </View>

          <Text numberOfLines={2} style={styles.lastMessage}>
            {t(strings.practiceDescription)}
          </Text>

          <View style={practiceCardStyles.trustRow}>
            <Ionicons name="lock-closed" size={11} color={colors.textMuted} />
            <Text style={[practiceCardStyles.trust, { color: colors.textMuted }]}>
              {t(strings.practicePrivate)}
            </Text>
          </View>
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t(strings.openPracticeA11y)}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        styles.row,
        practiceCardStyles.card,
        {
          backgroundColor: colors.primarySurface,
          borderColor: colors.primaryBorder,
        },
        Platform.OS === 'web' && hovered && styles.rowHovered,
        pressed && styles.rowPressed,
      ]}
    >
      {/* Icon + title share the top row; the subtitle sits full-width below so
          it uses the whole card instead of a narrow column beside the avatar. */}
      <View style={practiceCardStyles.topRow}>
        <View
          style={[
            practiceCardStyles.avatar,
            { backgroundColor: colors.primary },
          ]}
        >
          <Ionicons name="chatbubbles-outline" size={24} color={colors.white} />
        </View>

        <Text
          numberOfLines={1}
          style={[practiceCardStyles.title, { color: colors.textStrong }]}
        >
          {t(strings.practiceTitle)}
        </Text>

        <Ionicons name="chevron-forward" size={20} color={colors.textMuted} />
      </View>

      <Text
        numberOfLines={2}
        style={[practiceCardStyles.preview, { color: colors.textSecondary }]}
      >
        {t(strings.practiceDescription)}
      </Text>

      <View style={practiceCardStyles.trustRow}>
        <Ionicons name="lock-closed" size={11} color={colors.textMuted} />
        <Text style={[practiceCardStyles.trust, { color: colors.textMuted }]}>
          {t(strings.practicePrivate)}
        </Text>
      </View>
    </Pressable>
  );
}

const practiceCardStyles = StyleSheet.create({
  // Override the shared chat-row layout: stack vertically so text spans the
  // full card width rather than sitting in a column beside the avatar.
  card: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Sized to match the 54px avatars in the regular chat rows.
  avatarCompact: {
    width: 54,
    height: 54,
    borderRadius: 27,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    fontFamily: FONT_FAMILY,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '700',
  },
  preview: { fontSize: 14, lineHeight: 19 },
  trustRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  trust: { fontSize: 12, fontWeight: '600' },
});

/**
 * One half of the Active / Inactive switch.
 *
 * Deliberately a pair of buttons rather than a filter menu: the whole point of
 * splitting the inbox is that a dormant conversation is not gone, so the tab
 * holding it has to be visible at all times — including its count, which is the
 * only signal that anything is waiting over there.
 */
/**
 * One inbox tab. Text only, and deliberately.
 *
 * These were an icon plus a label plus an inline count, which costs 50dp of
 * chrome per tab before a single character is drawn: 26 padding, 2 border,
 * 16 icon, 6 icon gap. Two tabs carry that comfortably. A third does not —
 * on a 360dp phone three tabs get 104dp each, so 54dp of text, and the
 * rendered string was `Inactive 12`, which in the locales we ship is
 * `Inactives 12` / `Inactivas 12`. Roughly 90dp of text in a 54dp slot.
 *
 * It would not have failed loudly. `numberOfLines={1}` with `flexShrink: 1`
 * ellipsises, so French and Spanish would quietly read `Inactiv… 12`.
 *
 * The icon was the expensive part, not the padding: 22dp against the 10dp
 * that trimming the inset back to 8 recovers. Padding alone does not fit a
 * third tab; dropping the icon does.
 *
 * The count is gone from the LABEL, not from the tab. `a11yLabel` still
 * carries the total, because somebody using a screen reader cannot glance at
 * the list to see how many there are — the one reader for whom the number was
 * doing real work.
 */
function InboxTabButton({
  label,
  a11yLabel,
  selected,
  onPress,
  colors,
  styles,
}: {
  label: string;
  a11yLabel: string;
  selected: boolean;
  onPress: () => void;
  colors: CuesColors;
  styles: CuesStyles;
}) {
  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityLabel={a11yLabel}
      accessibilityState={{ selected }}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        styles.tab,
        selected && styles.tabSelected,
        Platform.OS === 'web' && hovered && styles.tabHovered,
        pressed && styles.tabPressed,
      ]}>
      <Text
        numberOfLines={1}
        style={[styles.tabLabel, selected && styles.tabLabelSelected]}>
        {label}
      </Text>
    </Pressable>
  );
}

/**
 * The one-line summary a row shows in place of the last message.
 *
 * Ordered by what the reader can act on. A pending reopen request outranks the
 * bare "Inactive" label because it is the only one of these states that is
 * waiting on somebody.
 */
function chatPreview(
  chat: ChatSession,
  t: (key: TranslationKey) => string,
): string {
  if (chat.state === 'inactive') {
    if (chat.reopenRequestedByPeer) return t(strings.reopenAsked);
    if (chat.reopenRequestedByMe) return t(strings.reopenWaiting);
    return t(strings.inactivePreview);
  }
  if (chat.active === false) return t(strings.noLongerActive);
  // `== null`, not falsy: an empty preview rendered blank before this line
  // existed and still has to. Translation changes what a row SAYS, never which
  // rows say something.
  if (chat.lastMessage == null) return t(strings.sayHi);
  // A system line is rendered from its key, so the row reads in the same
  // language as the thread it opens — the same rule the group rows below
  // follow. A human message has no key and passes through as typed.
  return systemMessageText({
    content: chat.lastMessage,
    systemKey: chat.lastMessageKey,
  });
}

function ChatRow({
  chat,
  unreadCount,
  nowMs,
  onPress,
  colors,
  styles,
}: {
  chat: ChatSession;
  unreadCount: number;
  /**
   * The clock this row judges the peer's Open lease against.
   *
   * Passed in rather than read here so every row in one list agrees about the
   * same instant, and so the row stays a pure function of its props — see the
   * note on `nowMs` in the screen.
   */
  nowMs: number;
  onPress: () => void;
  colors: CuesColors;
  styles: CuesStyles;
}) {
  const { i18n, t } = useTranslation();
  const visibleName = chat.peerName || t(strings.someone);
  const title = chat.peerVenueTag
    ? `${visibleName} · ${chat.peerVenueTag}`
    : visibleName;
  const goals = chat.peerLookingFor
    ?.filter(Boolean)
    .slice(0, 2)
    .join(' · ');
  const meta = [
    chat.peerAgeBand,
    goals ? t(strings.hereFor, { goals }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const hasUnread = unreadCount > 0;
  const dormant = chat.state === 'inactive';
  const preview = chatPreview(chat, t);
  // Shown in full rather than truncated: it is the only warning either person
  // gets, and the sentence that matters most is the last one.
  const warning = chat.inactiveWarning === true;
  const peerOpen = isOpenNow(chat.peerOpenUntil, nowMs);

  return (
    <Pressable
      accessibilityRole="button"
      // The arc is drawn on this row, so it has to be SAID on this row. Without
      // this a sighted reader sees who is free from the Active tab while a
      // screen-reader user has to go and find the Open tab to learn the same
      // thing — the signal existed, in a form only one of them could reach.
      //
      // Appended to the row's own label rather than announced separately: the
      // arc is not a control and not a second element, it is a fact about the
      // person this row is about, and a lone "open to chat" landing between two
      // rows belongs to neither.
      accessibilityLabel={[
        t(strings.openConversationA11y, { name: visibleName }),
        peerOpen ? t(strings.openPeerA11y) : null,
      ]
        .filter(Boolean)
        .join(', ')}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        styles.rowShell,
        Platform.OS === 'web' && hovered && styles.rowHovered,
        pressed && styles.rowPressed,
      ]}>
      <View style={styles.rowMain}>
        <View
          style={[styles.avatarWrap, dormant && styles.avatarWrapDormant]}>
          <Avatar
            seed={chat.peerAvatarSeed}
            avatarConfig={chat.peerAvatarConfig}
            name={chat.peerName}
            size={54}
            // This corner carries live presence in the inbox, so the
            // pin/vibe badge stays off the row entirely.
            showBadge={false}
            // The arc, on EVERY tab this row appears in. That is the whole
            // point of putting `peerOpenUntil` on Active rows: seeing who is
            // free costs no extra request and no trip to the Open tab. It
            // resolves to false on Inactive rows on its own, because the
            // server refuses to send a lease for a conversation that is not
            // writable.
            open={peerOpen}
          />

          {/* A dormant thread has no live presence to report. */}
          {dormant ? null : <View style={styles.onlineIndicator} />}
        </View>

        <View style={styles.info}>
          <View style={styles.nameRow}>
            <Text
              numberOfLines={1}
              style={styles.name}>
              {title}
            </Text>
            {/* Shown for a blocked peer too — the badge is a public fact about
                a person, and their name is shown either way. */}
          </View>

          {meta || chat.peerHeadline ? (
            <Text
              numberOfLines={1}
              style={styles.metaLine}>
              {chat.peerHeadline ?? meta}
            </Text>
          ) : null}

          <Text
            numberOfLines={1}
            style={
              dormant
                ? styles.lastMessageDormant
                : hasUnread
                  ? styles.lastMessageUnread
                  : styles.lastMessage
            }>
            {preview}
          </Text>
        </View>

        <View style={styles.rowMeta}>
          <Text style={styles.rowDate}>
            {formatInboxDate(chat.updatedAt, i18n.language)}
          </Text>
          {hasUnread ? (
            <View style={styles.unreadBadge}>
              <Text style={styles.unreadBadgeText}>
                {unreadCount}
              </Text>
            </View>
          ) : null}
        </View>
      </View>

      {warning ? (
        <View style={styles.rowNotice}>
          <Ionicons
            name="time-outline"
            size={14}
            color={colors.textSecondary}
          />

          <Text style={styles.rowNoticeText}>
            {t(strings.inboxInactiveWarning)}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

function GroupRow({
  group,
  onPress,
  colors,
  styles,
}: {
  group: SmallGroupInbox;
  onPress: () => void;
  colors: CuesColors;
  styles: CuesStyles;
}) {
  const { i18n, t } = useTranslation();
  const hasUnread = group.unreadCount > 0;
  const preview =
    // A system line is rendered from its key, so the row reads in the same
    // language as the thread it opens. Without this the list said "Alex
    // joined the group." in English above a translated conversation.
    (group.lastMessage
      ? systemMessageText({
          content: group.lastMessage,
          systemKey: group.lastMessageKey,
          systemParams: group.lastMessageParams,
        })
      : null) ??
    ((group.state ?? 'active') === 'inactive'
      ? // "0 minutes left" is the wrong sentence about a thread that is kept
        // precisely because the time ran out.
        t(strings.endedPreview)
      : t(strings.groupPreview, {
          count: group.memberCount,
          max: group.maxMembers,
          minutes: group.minutesLeft,
        }));
  const date = formatInboxDate(group.lastMessageAt, i18n.language);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t(strings.openGroupA11y, { name: group.name })}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        styles.row,
        Platform.OS === 'web' && hovered && styles.rowHovered,
        pressed && styles.rowPressed,
      ]}>
      <View style={styles.groupAvatar}>
        <Ionicons
          name="people"
          size={26}
          color={colors.primary}
        />
      </View>

      <View style={styles.info}>
        <View style={styles.nameRow}>
          <Text
            numberOfLines={1}
            style={styles.name}>
            {group.name}
          </Text>
        </View>

        <Text
          numberOfLines={1}
          style={styles.metaLine}>
          {group.vibe} · {t(strings.people, { count: group.memberCount })}
        </Text>

        <Text
          numberOfLines={1}
          style={hasUnread ? styles.lastMessageUnread : styles.lastMessage}>
          {preview}
        </Text>
      </View>

      <View style={styles.rowMeta}>
        <Text style={styles.rowDate}>{date}</Text>
        {hasUnread ? (
          <View style={styles.unreadBadge}>
            <Text style={styles.unreadBadgeText}>
              {group.unreadCount}
            </Text>
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}

/**
 * The Inactive tab with nothing in it.
 *
 * Explains the rule rather than apologising for the emptiness — this tab is
 * one most people will open exactly once, to find out what it is.
 */
/**
 * The Open tab with nobody in it — which is the NORMAL state, not a failure.
 *
 * Openness is measured in hours, so an empty list is what this tab looks like
 * most of the time. It says what would fill it and where else the signal shows
 * up, rather than apologising, and the CTA above it stays reachable.
 */
/**
 * The Open tab could not be read.
 *
 * A distinct state from "nobody is open", and the distinction is the whole
 * point: one is a fact about other people, the other is a fact about this
 * device's last request. Showing the first when the second is true makes a
 * working feature look dead — and it is a claim that quietly gets worse the
 * longer the network is unhappy, because nothing here retried on its own.
 *
 * The button is that retry, in place. Deliberately manual rather than a timer:
 * an automatic loop against a failing endpoint is how a quiet outage becomes a
 * loud one, and focus and pull-to-refresh already retry for free. One tap, and
 * the person who can see the problem decides when to try again.
 */
function OpenLoadFailed({
  onRetry,
  colors,
  styles,
}: {
  onRetry: () => Promise<void>;
  colors: CuesColors;
  styles: CuesStyles;
}) {
  const { t } = useTranslation();
  // Local, because it describes THIS button rather than the section. The store
  // already collapses concurrent reads, so repeated taps cost no extra request
  // — but a control that does not acknowledge a press reads as one that did
  // not register it, and the honest answer to "did my tap work" is to show it
  // working. Same shape as `OpenToggle`, which is the other control on this
  // tab that waits on a request.
  const [pending, setPending] = useState(false);

  return (
    <View style={[styles.empty, styles.inactiveEmpty]}>
      <Text style={styles.emptyTitle}>{t(strings.openFailedTitle)}</Text>
      <Text style={styles.emptyDescription}>{t(strings.openFailedBody)}</Text>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: pending, busy: pending }}
        disabled={pending}
        onPress={() => {
          setPending(true);
          // `finally`, not `then`: a retry that fails must release the button
          // as surely as one that succeeds, or the second attempt is
          // impossible. `onRetry` reports its own failure and never rejects.
          void onRetry().finally(() => setPending(false));
        }}
        style={[
          styles.openToggle,
          // Symmetric padding back. `openToggle` is lopsided on purpose — its
          // right side is tucked in for the switch — and this button has no
          // switch, so it would sit with a visibly short right edge.
          styles.openToggleFlat,
          {
            backgroundColor: colors.surface,
            borderColor: colors.border,
            opacity: pending ? 0.6 : 1,
          },
        ]}
      >
        <Text style={[styles.openToggleLabel, { color: colors.textPrimary }]}>
          {t(strings.tryAgain)}
        </Text>
      </Pressable>
    </View>
  );
}

function EmptyOpen({ styles }: { styles: CuesStyles }) {
  const { t } = useTranslation();
  return (
    <View style={[styles.empty, styles.inactiveEmpty]}>
      <Text style={styles.emptyTitle}>{t(strings.openEmpty)}</Text>
      <Text style={styles.emptyDescription}>{t(strings.openEmptyBody)}</Text>
    </View>
  );
}

/**
 * The "I'm Open" control. One switch, both directions.
 *
 * OFF it reads "I'm Open" and opens the duration sheet. ON it reads
 * "I'm Open · for 2h 15m" and turns itself off on the next tap — no sheet, no
 * confirmation. Making the ON state a second, differently-worded button
 * ("Turn off") would be clearer to read and worse to use: the thing you press
 * to start is the thing you press to stop, and the label carries the state.
 *
 * Shaped as the same switch row `home.tsx` uses for visibility, and sharing
 * its `CompactSwitch`. Both answer "am I discoverable, and for how long", and
 * a filled pill on one screen next to a switch on the other made them look
 * like two unrelated mechanisms. The row is the control and the switch only
 * displays — see `CompactSwitch` for why it is not a real `Switch`.
 *
 * `accessibilityRole` is "switch" rather than "button" now that it presents as
 * one, so `checked` is what announces the state; `selected` on a button is not
 * the same thing to a screen reader.
 *
 * The remaining time is computed from the LEASE against the clock, not counted
 * down. It therefore never drifts, survives the app being backgrounded, and
 * needs no timer — it re-reads whenever the screen renders, which for a tab
 * you have just opened is exactly when it matters.
 */
function OpenToggle({
  openUntil,
  nowMs,
  pending,
  onTurnOn,
  onTurnOff,
  colors,
  styles,
}: {
  openUntil: string | null;
  nowMs: number;
  pending: boolean;
  onTurnOn: () => void;
  onTurnOff: () => void;
  colors: CuesColors;
  styles: CuesStyles;
}) {
  const { t } = useTranslation();
  const on = isOpenNow(openUntil, nowMs);
  const label = on
    ? t(strings.openImOpenFor, {
        time: formatCompactDuration(openMinutesLeft(openUntil, nowMs)),
      })
    : t(strings.openImOpen);

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked: on, disabled: pending, busy: pending }}
      accessibilityLabel={
        on ? t(strings.openTurnOffA11y) : t(strings.openImOpenA11y)
      }
      disabled={pending}
      onPress={on ? onTurnOff : onTurnOn}
      style={({ pressed }) => [
        styles.openToggle,
        {
          // Never filled green. The switch beside it is green when on, and a
          // green switch on a green field is a switch you cannot read.
          backgroundColor: on ? colors.greenSoft : colors.primarySurface,
          borderColor: on ? colors.greenBorder : colors.primaryBorder,
          opacity: pending ? 0.6 : 1,
        },
        pressed && styles.openTogglePressed,
      ]}
    >
      <Ionicons
        name={on ? 'chatbubbles' : 'chatbubbles-outline'}
        size={17}
        color={on ? colors.green : colors.iconNeutral}
      />

      <Text
        numberOfLines={1}
        style={[
          styles.openToggleLabel,
          { color: on ? colors.green : colors.textPrimary },
        ]}
      >
        {label}
      </Text>

      <CompactSwitch enabled={on} disabled={pending} />
    </Pressable>
  );
}

/**
 * How long to stay open for.
 *
 * The same shape as the Small Group duration picker rather than a date picker:
 * four fixed choices, one tap, done. A duration this short does not need a
 * spinner, and choosing IS confirming — there is no second button, because the
 * mistake it would guard against is undone by tapping the toggle again.
 *
 * No "until I turn it off". A signal that says "free right now" and then runs
 * for a week is a claim the person forgot they made, and it is the one failure
 * this whole feature has to avoid.
 */
function OpenDurationSheet({
  visible,
  onDismiss,
  onChoose,
  colors,
  styles,
}: {
  visible: boolean;
  onDismiss: () => void;
  onChoose: (minutes: number) => void;
  colors: CuesColors;
  styles: CuesStyles;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <Pressable style={styles.openSheetBackdrop} onPress={onDismiss} />
      <View style={styles.openSheetCentre} pointerEvents="box-none">
        <View
          style={[
            styles.openSheet,
            { backgroundColor: colors.surface, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.openSheetTitle, { color: colors.textPrimary }]}>
            {t(strings.openDurationTitle)}
          </Text>
          {OPEN_TO_CHAT_MINUTES.map((minutes) => (
            <Pressable
              key={minutes}
              accessibilityRole="button"
              onPress={() => onChoose(minutes)}
              style={[styles.openSheetOption, { borderColor: colors.border }]}
            >
              <Text
                style={[
                  styles.openSheetOptionLabel,
                  { color: colors.textPrimary },
                ]}
              >
                {minutes < 60
                  ? t(strings.openDurationMinutes, { count: minutes })
                  : minutes === 60
                    ? t(strings.openDurationHour)
                    : t(strings.openDurationHours, { count: minutes / 60 })}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </Modal>
  );
}

function EmptyInactive({ styles }: { styles: CuesStyles }) {
  const { t } = useTranslation();
  return (
    <View style={[styles.empty, styles.inactiveEmpty]}>
      <Text style={styles.emptyTitle}>
        {t(strings.inactiveEmptyTitle)}
      </Text>

      <Text style={styles.emptyDescription}>
        {t(strings.inactiveEmptyBody)}
      </Text>
    </View>
  );
}

function EmptyCues({
  colors,
  styles,
  onPractice,
}: {
  colors: CuesColors;
  styles: CuesStyles;
  onPractice: () => void;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.empty}>
      {/* Conversation Practice lives at the top of the single Inbox card. */}
      <View style={styles.emptyPracticeSlot}>
        <PracticeInboxCard
          colors={colors}
          styles={styles}
          onPress={onPractice}
          compact={Platform.OS === 'web'}
        />
      </View>

      <View style={styles.emptyGraphic}>
        <View
          style={styles.emptyOuterRing}
        />

        <View
          style={styles.emptyMiddleRing}
        />

        <View style={styles.emptyIcon}>
          <Ionicons
            name="chatbubbles-outline"
            size={31}
            color={colors.primary}
          />
        </View>

        <View
          style={styles.emptyNotification}>
          <Ionicons
            name="add"
            size={16}
            color={colors.white}
          />
        </View>
      </View>

      <Text style={styles.emptyTitle}>
        {t(strings.inboxEmptyTitle)}
      </Text>

      <Text style={styles.emptyDescription}>
        {t(strings.inboxEmptyBody)}
      </Text>

    </View>
  );
}

function createStyles(
  colors: CuesColors,
  isDark: boolean,
  // Web, on a phone-sized viewport. Only ever routes narrow web to the branch
  // native already takes — it never introduces a third layout.
  compact: boolean,
) {
  // `flex: 1` is what makes each filter chip a third of the bar instead of
  // shrinking to its text. Native has always had it; wide web deliberately
  // does not, because three stretched chips across a desktop header look
  // absurd. Narrow web wants the native answer, and this is the whole of the
  // difference between the two.
  const stretchFilters = Platform.OS !== 'web' || compact;
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.screen,
    },

    topBar: {
      minHeight: 62,
      paddingHorizontal: 16,
      paddingVertical: 11,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottomWidth:
        StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: colors.topBar,
    },

    topBarLogo: {
      position: 'absolute',
      left: 24,
    },

    tabs: {
      flex: stretchFilters ? 1 : undefined,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },

    tab: {
      flex: stretchFilters ? 1 : undefined,
      minHeight: 40,
      // 8, down from 13. The button is `flex: 1`, so this shrinks the inset
      // around the text, never the tap target — which stays 40dp tall and a
      // third of the bar wide. See `InboxTabButton` for the arithmetic.
      paddingHorizontal: 8,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 13,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },

    tabSelected: {
      borderColor: BRAND_BLUE,
      backgroundColor: BRAND_BLUE,
      shadowColor: BRAND_BLUE_SHADOW,
      shadowOpacity: 0.2,
      shadowRadius: 9,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 3,
    },

    tabHovered: {
      borderColor: colors.primaryBorder,
    },

    tabPressed: {
      backgroundColor: colors.surfacePressed,
    },

    tabLabel: {
      flexShrink: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: '700',
    },

    tabLabelSelected: {
      color: colors.white,
    },

    infoCard: {
      minHeight: 62,
      paddingHorizontal: 16,
      paddingVertical: 14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 13,
      borderRadius: 18,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.divider,
      backgroundColor: colors.surfaceMuted,
    },

    infoIcon: {
      width: 43,
      height: 43,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.primarySoft,
    },

    infoCopy: {
      flex: 1,
    },

    infoDescription: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: '600',
    },

    list: {
      width: '100%',
      maxWidth: 760,
      alignSelf: 'center',
      paddingHorizontal: 16,
      paddingTop: 18,
      paddingBottom: 55,
      gap: 0,
    },

    emptyList: {
      flexGrow: 1,
    },

    row: {
      minHeight: 82,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      borderRadius: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: 'transparent',
    },

    // A conversation row is a column, not a row: the 29-day warning needs the
    // full width underneath the usual avatar/preview line. `row` above stays as
    // it is for the practice card and group rows, which never carry a notice.
    rowShell: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      gap: 8,
      borderRadius: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: 'transparent',
    },

    rowMain: {
      minHeight: 62,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },

    rowNotice: {
      paddingHorizontal: 10,
      paddingVertical: 8,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 7,
      borderRadius: 10,
      backgroundColor: colors.surfaceMuted,
    },

    rowNoticeText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
      fontWeight: '600',
    },

    rowHovered: {
      backgroundColor: colors.surfaceMuted,
    },

    rowPressed: {
      backgroundColor: colors.surfacePressed,
    },

    avatarWrap: {
      position: 'relative',
    },

    // Dimmed rather than greyscaled: enough to read as "resting" at a glance
    // without making the person look blocked or deleted.
    avatarWrapDormant: {
      opacity: 0.55,
    },

    // Blue, not green. Worth knowing what that costs: this dot means "online
    // right now" and the arc above it means "open to chat", which are different
    // facts about the same person, and they now share a colour. They stay
    // distinguishable by SHAPE — a filled dot on the rim versus a fading arc
    // over the top — rather than by hue, which is the weaker of the two
    // distinctions but the one that was asked for, and it does buy a row that
    // reads as one signal instead of two competing ones.
    onlineIndicator: {
      position: 'absolute',
      right: 0,
      bottom: 1,
      width: 14,
      height: 14,
      borderRadius: 7,
      borderWidth: 3,
      borderColor: colors.surface,
      backgroundColor: colors.primary,
    },

    info: {
      flex: 1,
      minWidth: 0,
      gap: 3,
    },

    nameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
    },

    name: {
      flexShrink: 1,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 18,
      lineHeight: 23,
      fontWeight: '800',
    },

    revealedBadge: {
      minHeight: 23,
      paddingHorizontal: 7,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRadius: 12,
      backgroundColor: colors.primarySoft,
    },

    revealedText: {
      color: colors.primaryStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 9,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },

    anonymousBadge: {
      minHeight: 23,
      paddingHorizontal: 7,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRadius: 12,
      backgroundColor: colors.surfaceMuted,
    },

    anonymousText: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 9,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },

    lastMessage: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
    },

    lastMessageDormant: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontStyle: 'italic',
    },

    lastMessageUnread: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: '700',
    },

    groupAvatar: {
      width: 54,
      height: 54,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySoft,
    },

    groupBadge: {
      minHeight: 23,
      paddingHorizontal: 7,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRadius: 12,
      backgroundColor: colors.primarySoft,
    },

    groupBadgeText: {
      color: colors.primaryStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 9,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },

    unreadBadge: {
      minWidth: 24,
      height: 24,
      paddingHorizontal: 7,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.primary,
    },

    unreadBadgeText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: '800',
    },

    metaLine: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: '600',
    },

    detailLine: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
    },

    interestsLine: {
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: '700',
    },

    chevronWrap: {
      width: 36,
      height: 36,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.primarySoft,
    },

    rowMeta: {
      minWidth: 76,
      maxWidth: 92,
      alignSelf: 'stretch',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      gap: 8,
      paddingTop: 2,
    },

    rowDate: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: '700',
      textAlign: 'right',
    },

    empty: {
      flex: 1,
      minHeight: 420,
      paddingHorizontal: 0,
      paddingVertical: Platform.OS === 'web' ? 0 : 24,
      alignItems: 'center',
      position: 'relative',
      justifyContent:
        Platform.OS === 'web'
          ? 'center'
          : 'flex-start',
      borderRadius: 0,
      borderWidth: 0,
      borderColor: colors.border,
      backgroundColor: 'transparent',
      shadowColor: colors.shadow,
      shadowOpacity: 0,
      shadowRadius: 0,
      shadowOffset: {
        width: 0,
        height: 0,
      },
      elevation: 0,
    },

    inactiveEmpty: {
      justifyContent: 'center',
    },

    // ---- Open to chat ----------------------------------------------------
    // The heading and toggle sit in the list HEADER, not above the list, so
    // they scroll away with the rows rather than pinning a permanent block of
    // chrome to a tab whose content is usually short.
    openHeader: {
      paddingBottom: 14,
      gap: 10,
    },
    openHeading: {
      fontSize: 15,
      fontWeight: '700',
      color: colors.textStrong,
    },
    // A row, because it now holds an icon, a label and a switch. Still
    // `alignSelf: 'flex-start'` so it hugs its content rather than stretching
    // across the header — the label's width changes as the countdown steps
    // down, and a full-width control would leave the switch drifting.
    openToggle: {
      alignSelf: 'flex-start',
      minHeight: 44,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingLeft: 14,
      paddingRight: 8,
      borderRadius: 22,
      borderWidth: 1,
    },
    openTogglePressed: {
      opacity: 0.78,
    },
    openToggleFlat: {
      paddingLeft: 18,
      paddingRight: 18,
    },
    openToggleLabel: {
      // Shrinks rather than pushing the switch off the edge on a narrow phone
      // at the longest label ("I'm Open · for 2h 59m").
      flexShrink: 1,
      fontSize: 15,
      fontWeight: '700',
    },
    openSheetBackdrop: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundColor: 'rgba(6, 14, 26, 0.45)',
    },
    openSheetCentre: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    },
    openSheet: {
      width: '100%',
      maxWidth: 340,
      borderRadius: 18,
      borderWidth: 1,
      paddingVertical: 8,
      paddingHorizontal: 8,
    },
    openSheetTitle: {
      fontSize: 15,
      fontWeight: '700',
      paddingHorizontal: 12,
      paddingTop: 10,
      paddingBottom: 6,
    },
    openSheetOption: {
      minHeight: 48,
      justifyContent: 'center',
      paddingHorizontal: 12,
      borderTopWidth: StyleSheet.hairlineWidth,
    },
    openSheetOptionLabel: {
      fontSize: 16,
      fontWeight: '600',
    },

    emptyPracticeSlot: {
      alignSelf: 'stretch',
      marginTop: 0,
      marginBottom: Platform.OS === 'web' ? 0 : 28,
      ...(Platform.OS === 'web'
        ? {
            position: 'absolute',
            top: 18,
            left: 0,
            right: 0,
            zIndex: 1,
          }
        : null),
    },

    headerPracticeSlot: {
      alignSelf: 'stretch',
      marginTop: 4,
      // Sits flush with the chat rows below it (same width, same list gap) so the
      // practice entry reads as the first conversation rather than a wide banner.
      marginBottom: 13,
    },

    emptyGraphic: {
      width: 116,
      height: 116,
      marginBottom: 22,
      alignItems: 'center',
      justifyContent: 'center',
    },

    emptyOuterRing: {
      position: 'absolute',
      width: 116,
      height: 116,
      borderRadius: 58,
      borderWidth: 1,
      borderColor: isDark
        ? 'rgba(110,168,255,0.21)'
        : 'rgba(60,117,210,0.11)',
    },

    emptyMiddleRing: {
      position: 'absolute',
      width: 84,
      height: 84,
      borderRadius: 42,
      backgroundColor: colors.primarySoft,
    },

    emptyIcon: {
      width: 58,
      height: 58,
      borderRadius: 29,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark
        ? 0.22
        : 0.13,
      shadowRadius: 11,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 3,
    },

    emptyNotification: {
      position: 'absolute',
      right: 20,
      bottom: 21,
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 3,
      borderColor: colors.surface,
      backgroundColor: colors.primary,
    },

    emptyTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 22,
      lineHeight: 28,
      fontWeight: '700',
      textAlign: 'center',
    },

    emptyDescription: {
      maxWidth: 330,
      marginTop: 9,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 22,
      textAlign: 'center',
    },

    emptyHint: {
      minHeight: 38,
      marginTop: 21,
      paddingHorizontal: 14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      borderRadius: 19,
      backgroundColor: colors.primarySoft,
    },

    emptyHintText: {
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: '600',
    },
  });
}
